/* Cliente de la API del panel. Mismo origen, así que la cookie de sesión viaja sola; un 401
 * avisa a `SesionProvider` para que muestre el inicio de sesión. `useDatos` mantiene los datos anteriores mientras recarga, para que la
 * pantalla no parpadee al cambiar un filtro. */
import { useCallback, useEffect, useRef, useState } from "react";

export class ErrorApi extends Error {
  constructor(
    message: string,
    readonly estado: number,
  ) {
    super(message);
  }
}

export async function api<T>(camino: string, opciones: { metodo?: "GET" | "POST" | "PATCH"; cuerpo?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  let r: Response;
  try {
    r = await fetch(camino, {
      method: opciones.metodo ?? "GET",
      // x-klavia-panel: el servidor rechaza escrituras sin él (protección contra CSRF, ver src/panel/auth.ts).
      headers: { "x-klavia-panel": "1", ...(opciones.cuerpo === undefined ? {} : { "content-type": "application/json" }) },
      body: opciones.cuerpo === undefined ? undefined : JSON.stringify(opciones.cuerpo),
      credentials: "same-origin",
      signal: opciones.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ErrorApi("No hay conexión con el servidor. Revisá la red y probá de nuevo.", 0);
  }
  const texto = await r.text();
  let datos: unknown = null;
  try {
    datos = texto ? JSON.parse(texto) : null;
  } catch {
    datos = null;
  }
  if (r.status === 401 && !camino.startsWith("/api/sesion/")) window.dispatchEvent(new Event("prospeccion:sin-sesion"));
  if (!r.ok) {
    const mensaje = (datos as { error?: string } | null)?.error ?? `El servidor respondió ${r.status}.`;
    throw new ErrorApi(mensaje, r.status);
  }
  return datos as T;
}

/* Avisa a quien muestre contadores (la barra lateral) que algo cambió. */
export function avisarCambio() {
  window.dispatchEvent(new Event("prospeccion:cambio"));
}

export function useAlCambiar(f: () => void) {
  const ref = useRef(f);
  ref.current = f;
  useEffect(() => {
    const oyente = () => ref.current();
    window.addEventListener("prospeccion:cambio", oyente);
    return () => window.removeEventListener("prospeccion:cambio", oyente);
  }, []);
}

export function useDatos<T>(camino: string | null) {
  const [datos, setDatos] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(camino !== null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (camino === null) return;
    const ctrl = new AbortController();
    setCargando(true);
    api<T>(camino, { signal: ctrl.signal })
      .then((d) => {
        setDatos(d);
        setError(null);
      })
      .catch((e: Error) => {
        if (e.name !== "AbortError") setError(e.message);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setCargando(false);
      });
    return () => ctrl.abort();
  }, [camino, version]);

  const recargar = useCallback(() => setVersion((v) => v + 1), []);
  return { datos, error, cargando, recargar, setDatos };
}
